// iter149 — Daily Entry offline queue backed by IndexedDB (via idb-keyval).
// Shape per queued item:
//   { entryId, schoolId, date, payload, createdAt, attempts }
// Rules:
//   - Each queued entry carries a client-generated UUIDv4 entryId. The
//     Daily Entry save endpoint upserts by `?id=<entryId>` so re-posting
//     the same payload is idempotent and never duplicates.
//   - Flush replays only items whose `schoolId` matches the current user
//     (safety on shared phones). Items for a different school are dropped
//     with a toast ("Dropped N offline entries from a different school").
//   - After 5 failed attempts an item is marked "stuck" and surfaces a
//     "Couldn't sync — tap to retry" banner. We never silently drop.
//   - Logout wipes the entire queue (see AuthContext.purgeStaleSession).
import { createStore, get, set, del, keys } from "idb-keyval";

const QUEUE_STORE = createStore("mdm-sahayak-offline-v1", "daily-entry-queue");
const MAX_ATTEMPTS = 5;

export function newEntryId() {
  // Prefer the browser's crypto.randomUUID when available (iOS 15.4+, all
  // modern Androids). Fallback for the handful of pre-Safari-15 users.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export async function enqueueDailyEntry({ entryId, schoolId, date, payload }) {
  const item = {
    entryId,
    schoolId,
    date,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    stuck: false,
  };
  await set(entryId, item, QUEUE_STORE);
  return item;
}

export async function listQueuedEntries(schoolIdFilter) {
  const ks = await keys(QUEUE_STORE);
  const out = [];
  for (const k of ks) {
    const v = await get(k, QUEUE_STORE);
    if (!v) continue;
    if (schoolIdFilter && v.schoolId !== schoolIdFilter) continue;
    out.push(v);
  }
  return out.sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeQueuedEntry(entryId) {
  await del(entryId, QUEUE_STORE);
}

export async function clearAllQueuedEntries() {
  const ks = await keys(QUEUE_STORE);
  await Promise.all(ks.map((k) => del(k, QUEUE_STORE)));
}

export async function markAttempt(entryId, success) {
  const v = await get(entryId, QUEUE_STORE);
  if (!v) return null;
  if (success) {
    await del(entryId, QUEUE_STORE);
    return null;
  }
  const next = { ...v, attempts: (v.attempts || 0) + 1 };
  if (next.attempts >= MAX_ATTEMPTS) next.stuck = true;
  await set(entryId, next, QUEUE_STORE);
  return next;
}

// Flush the queue — posts each item through the provided `postFn`
// (dailyEntryApi.upsert(payload, entryId)). Items for OTHER schools are
// dropped. Returns a summary {sent, dropped, failed, stuck}.
export async function flushQueue({ schoolId, postFn, onProgress }) {
  const items = await listQueuedEntries();
  let sent = 0; let dropped = 0; let failed = 0; let stuck = 0;
  for (const item of items) {
    if (schoolId && item.schoolId !== schoolId) {
      await del(item.entryId, QUEUE_STORE);
      dropped += 1;
      continue;
    }
    if (item.stuck) { stuck += 1; continue; }
    try {
      await postFn(item.payload, item.entryId);
      await del(item.entryId, QUEUE_STORE);
      sent += 1;
    } catch (err) {
      const next = await markAttempt(item.entryId, false);
      failed += 1;
      if (next?.stuck) stuck += 1;
    }
    if (typeof onProgress === "function") onProgress({ sent, dropped, failed, stuck });
  }
  return { sent, dropped, failed, stuck };
}
